import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { FactoringProvider } from 'services/factoring'

interface FactoringProviderStore {
    provider: FactoringProvider | null
    setProvider: (p: FactoringProvider | null) => void
}

// One-time migration: previous releases wrote the provider as a bare string
// under the `selectedFactoring` localStorage key. We now persist under
// `factoringProvider` via Zustand's `persist` middleware (which uses a JSON
// envelope shape). Read the legacy key once and seed the new store before
// `persist` rehydrates. Leaves the legacy key in place for one release so a
// rollback would still find the user's provider — drop the legacy read after
// every active deployment has consumed it.
const LEGACY_KEY = 'selectedFactoring'
const legacyValue = (() => {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(LEGACY_KEY)
    if (!raw) return null
    // Accept only known enum values to avoid hydrating a corrupted state.
    return Object.values(FactoringProvider).includes(raw as FactoringProvider) ? (raw as FactoringProvider) : null
})()

export const useFactoringProviderStore = create<FactoringProviderStore>()(
    persist(
        (set) => ({
            provider: legacyValue,
            setProvider: (provider) => set({ provider }),
        }),
        {
            name: 'factoringProvider',
            storage: createJSONStorage(() => localStorage),
            version: 1,
        }
    )
)

// Imperative accessors for non-React callers. `getProvider`/`setProvider` keep
// the same name as the previous module-level API so the migration to Zustand
// is a contained internal change.
export const getProvider = (): FactoringProvider | null => useFactoringProviderStore.getState().provider
export const setProvider = (p: FactoringProvider | null): void => useFactoringProviderStore.getState().setProvider(p)
